import React, { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";

// Preparing a grant does not join RTC, consent, open devices or start providers.
export default function AcceptanceActivation({ streamId }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const operating = useRef(false);
  const alive = useRef(true);
  const path = `/livestreams/${encodeURIComponent(streamId)}/voice-acceptance-grant`;
  const refresh = async () => {
    const next = await api(path, { auth: true });
    if (alive.current) setInfo(next);
  };
  useEffect(() => {
    alive.current = true;
    refresh().catch(() => { if (alive.current) setError("Private activation status is unavailable. No session was prepared."); });
    return () => { alive.current = false; };
    // Mounted with a room-specific key by the setup page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  const act = async kind => {
    if (operating.current || (kind === "issue" && (attempted || !info?.can_issue))) return;
    operating.current = true; setBusy(true); setError("");
    if (kind === "issue") setAttempted(true);
    try {
      if (kind !== "refresh") await api(kind === "issue" ? path : `${path}/revoke`, { auth: true, method: "POST" });
      await refresh();
    } catch (_) {
      if (alive.current) setError("Activation could not be confirmed. Refresh status; no automatic grant or voice retry was made.");
    } finally {
      operating.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return <section aria-label="Private acceptance activation" className="tt-panel p-4 space-y-3">
    <h3>Prepare one private AI session</h3>
    <p>One owner only, 60 seconds including startup and cleanup, two AI attempts and two replies of at most 300 characters each across the entire cast. No automatic retries. These limits are not a monetary cap.</p>
    <p role="status">{info ? `Session permission: ${info.status}.` : "Checking private session permission."}</p>
    {info?.readiness?.checks && <section aria-label="Production configuration checks">
      <h4>Production configuration checks (no provider calls)</h4>
      <ul>{info.readiness.checks.map(check => <li key={check.name}>
        {check.name}: {check.valid ? 'Valid' : 'Blocked'}. {check.detail}
      </li>)}</ul>
      <p>Configuration checks do not verify live service connectivity or reserve a worker.</p>
    </section>}
    {info && !info.can_issue && info.status === "not_issued" && <p>Preparation requires operator approval for this exact private room, owner, budget and issuance window, configured character voices, and an available worker.</p>}
    {info?.expires_at && <p>Permission expires: {info.expires_at}</p>}
    <p>Preparing does not activate your microphone or camera, consent for you, or start AI. After preparation, join muted, give consent, and use the room's microphone and Start AI controls yourself.</p>
    <button disabled={busy || attempted || !info?.can_issue} onClick={() => act("issue")} className="border rounded p-2">Prepare single-use session</button>
    <button disabled={busy} onClick={() => act("refresh")} className="border rounded p-2">Refresh permission status</button>
    {info && info.status !== "not_issued" && <button disabled={busy} onClick={() => act("revoke")} className="border rounded p-2">Revoke session permission and stop AI</button>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
